import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Eye } from "lucide-react";
import HamburgerMenu from "@/components/HamburgerMenu";
import Logo from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function fmtDate(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

const MD_COMPONENTS = {
    h1: (props) => (
        <h1 className="mt-10 text-3xl font-semibold tracking-tight text-[#0A0A0A]" {...props} />
    ),
    h2: (props) => (
        <h2 className="mt-10 text-2xl font-semibold tracking-tight text-[#0A0A0A]" {...props} />
    ),
    h3: (props) => (
        <h3 className="mt-8 text-xl font-semibold tracking-tight text-[#0A0A0A]" {...props} />
    ),
    p: (props) => (
        <p className="mt-5 text-[16px] leading-[1.75] text-[#0A0A0A]" {...props} />
    ),
    ul: (props) => <ul className="mt-5 list-disc space-y-2 pl-6 text-[16px]" {...props} />,
    ol: (props) => <ol className="mt-5 list-decimal space-y-2 pl-6 text-[16px]" {...props} />,
    li: (props) => <li className="leading-relaxed text-[#0A0A0A]" {...props} />,
    a: (props) => (
        <a className="text-[#0A0A0A] underline underline-offset-[3px] decoration-[#0A0A0A] transition hover:text-[#737373]" {...props} />
    ),
    blockquote: (props) => (
        <blockquote className="my-6 border-l-2 border-[#0A0A0A] pl-5 text-[#525252] italic" {...props} />
    ),
    img: (props) => (
        <img
            className="my-8 w-full rounded-xl border border-[#E5E5E5]"
            loading="lazy"
            alt=""
            {...props}
        />
    ),
    hr: () => <hr className="my-10 border-t border-[#E5E5E5]" />,
    code: (props) => (
        <code className="rounded bg-[#F5F5F5] px-1.5 py-0.5 text-[13.5px] font-mono text-[#0A0A0A]" {...props} />
    ),
};

export default function BlogPostPage() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [related, setRelated] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setNotFound(false);
        axios
            .get(`${API}/blog/${slug}`)
            .then((r) => {
                if (cancelled) return;
                setPost(r.data);
                setRelated(r.data?.related || []);
            })
            .catch((e) => {
                if (!cancelled && e?.response?.status === 404) setNotFound(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [slug]);

    if (notFound) {
        return (
            <div className="flex min-h-screen flex-col bg-white text-[#0A0A0A]">
                <Header />
                <main className="mx-auto flex w-full max-w-[820px] flex-1 flex-col items-center justify-center px-5 py-24 text-center">
                    <h1 className="text-3xl font-semibold tracking-tight">Post not found</h1>
                    <p className="mt-3 text-[15px] text-[#737373]">
                        The article you're looking for has been moved or unpublished.
                    </p>
                    <Link
                        to="/blog"
                        className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#0A0A0A] px-5 py-2.5 text-[13px] font-medium text-white transition hover:bg-[#1F1F1F]"
                        data-testid="blog-back-from-404"
                    >
                        <ArrowLeft size={13} strokeWidth={2} /> Back to blog
                    </Link>
                </main>
            </div>
        );
    }

    if (loading || !post) {
        return (
            <div className="flex min-h-screen flex-col bg-white text-[#0A0A0A]">
                <Header />
                <main className="mx-auto w-full max-w-[820px] flex-1 px-5 py-12">
                    <div className="h-[400px] animate-pulse rounded-2xl bg-[#F5F5F5]" />
                    <div className="mt-8 h-10 w-2/3 animate-pulse rounded bg-[#F5F5F5]" />
                    <div className="mt-4 h-5 w-1/2 animate-pulse rounded bg-[#F5F5F5]" />
                </main>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen flex-col bg-white text-[#0A0A0A]">
            <Header />

            <article
                className="mx-auto w-full max-w-[1200px] flex-1 px-5 py-10"
                data-testid="blog-post"
            >
                <div className="mx-auto max-w-[820px]">
                    {post.cover_image_url && (
                        <div className="mx-auto -mt-2 max-h-[420px] overflow-hidden rounded-2xl border border-[#E5E5E5]">
                            <img
                                src={post.cover_image_url}
                                alt=""
                                className="h-full max-h-[420px] w-full object-cover"
                            />
                        </div>
                    )}

                    <div className="mt-8 flex flex-wrap items-center gap-3 text-[12px]">
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-3 py-1 font-medium text-[#0A0A0A]">
                            <span className="text-[14px] leading-none">{post.country_flag}</span>
                            {post.country_name}
                        </span>
                        <span className="text-[#A3A3A3]">{fmtDate(post.published_at)}</span>
                        {post.view_count != null && (
                            <span className="inline-flex items-center gap-1 text-[#A3A3A3]">
                                <Eye size={11} strokeWidth={2} />
                                {post.view_count.toLocaleString()}
                            </span>
                        )}
                    </div>

                    <h1
                        className="mt-5 text-4xl font-semibold leading-[1.15] tracking-tight sm:text-[44px]"
                        data-testid="blog-post-title"
                    >
                        {post.title}
                    </h1>
                    {post.subtitle && (
                        <p className="mt-4 text-[18px] leading-relaxed text-[#525252]">
                            {post.subtitle}
                        </p>
                    )}
                    <p className="mt-6 text-[13px] text-[#737373]">By {post.author_name}</p>
                </div>

                <div className="mt-12 grid gap-12 lg:grid-cols-[minmax(0,1fr)_280px]">
                    <div className="mx-auto w-full max-w-[720px]" data-testid="blog-post-body">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
                            {post.body_markdown}
                        </ReactMarkdown>

                        <div className="mt-12 flex flex-wrap items-center gap-2">
                            {(post.tags || []).map((t) => (
                                <span
                                    key={t}
                                    className="rounded-full border border-[#E5E5E5] bg-white px-3 py-1 text-[11px] uppercase tracking-wider text-[#737373]"
                                >
                                    #{t}
                                </span>
                            ))}
                        </div>

                        <div className="mt-12 border-t border-[#E5E5E5] pt-8">
                            <Link
                                to="/blog"
                                className="inline-flex items-center gap-2 rounded-full border border-[#0A0A0A] bg-white px-4 py-2 text-[13px] font-medium text-[#0A0A0A] transition hover:bg-[#0A0A0A] hover:text-white"
                                data-testid="blog-back-to-list"
                            >
                                <ArrowLeft size={13} strokeWidth={2} /> All posts
                            </Link>
                        </div>
                    </div>

                    {related.length > 0 && (
                        <aside
                            className="hidden lg:block"
                            data-testid="blog-related"
                        >
                            <div className="sticky top-20">
                                <h3 className="text-[11px] uppercase tracking-[0.18em] text-[#A3A3A3]">
                                    More from {post.country_name}
                                </h3>
                                <ul className="mt-4 space-y-4">
                                    {related.map((r) => (
                                        <li key={r.id}>
                                            <Link
                                                to={`/blog/${r.slug}`}
                                                data-testid={`blog-related-${r.slug}`}
                                                className="group block"
                                            >
                                                <h4 className="text-[14px] font-medium leading-snug tracking-tight text-[#0A0A0A] underline-offset-[3px] decoration-transparent transition group-hover:underline group-hover:decoration-[#0A0A0A]">
                                                    {r.title}
                                                </h4>
                                                <p className="mt-1 text-[11px] text-[#A3A3A3]">
                                                    {fmtDate(r.published_at)}
                                                </p>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </aside>
                    )}
                </div>
            </article>

            <footer className="border-t border-[#E5E5E5] py-6 text-center text-[11px] text-[#A3A3A3]">
                Piqpath · Your trip. Your way.
            </footer>
        </div>
    );
}

function Header() {
    return (
        <header className="sticky top-0 z-10 border-b border-[#E5E5E5] bg-white/90 backdrop-blur">
            <div className="mx-auto flex w-full max-w-[1200px] items-center justify-between px-5 py-3">
                <div className="flex items-center gap-2">
                    <HamburgerMenu />
                    <Logo size="sm" />
                </div>
                <Link
                    to="/blog"
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-3 py-1 text-[12px] text-[#0A0A0A] transition hover:border-[#0A0A0A] hover:bg-[#F5F5F5]"
                    data-testid="blog-up-to-list"
                >
                    <ArrowLeft size={12} strokeWidth={2.25} /> All posts
                </Link>
            </div>
        </header>
    );
}
